import XLSX from 'xlsx'

interface ImportSuccess {
  total: number
}

interface ImportError {
  type: 'WrongImportHeader' | 'WrongImportInput'
  column: string
  row?: number
}

interface ImportData {
  success?: ImportSuccess
  error?: ImportError
}

export function parseImportResponse({success, error}: ImportData) {
  if (success) {
    return {
      message: 'import.targets.success',
      messageData: {total: success.total},
      error: false,
    }
  }

  if (!error) {
    return {
      message: 'import.success-import-text',
      error: false,
    }
  }

  if (error.type === 'WrongImportHeader') {
    return {
      message: 'import.errors.wrong-column',
      messageData: {column: error.column},
      error: true,
    }
  }

  if (error.type === 'WrongImportInput') {
    return {
      message: 'import.errors.wrong-row-column',
      messageData: {column: error.column, row: error.row},
      error: true,
    }
  }

  return {
    message: 'import.error-import-server',
    error: true,
  }
}

/**
 * Validates if a file is a valid XLSX (Excel) file
 * Performs two-layer validation:
 * 1. Checks file signature (magic numbers) to ensure it's a valid ZIP file (XLSX files are ZIP archives)
 * 2. Attempts to parse the file with XLSX library and verifies it contains at least one sheet
 *
 * @param {ArrayBuffer} data - The file data as ArrayBuffer
 * @returns {boolean} - Returns true if the file is a valid XLSX file
 * @throws {Error} - Throws an error if the file is not a valid XLSX file
 */
export function validateXlsxFile(data: ArrayBuffer): boolean {
  const uint8Array = new Uint8Array(data)

  // Check ZIP file signature (magic numbers)
  // XLSX files are ZIP archives that must start with "PK" (0x50 0x4B)
  if (uint8Array.length < 4 || uint8Array[0] !== 0x50 || uint8Array[1] !== 0x4b) {
    return false
  }

  // Additional ZIP validation: check the following bytes
  // Valid ZIP files have 0x03 0x04 (local file) or 0x05 0x06 (central directory)
  const isValidZip =
    (uint8Array[2] === 0x03 && uint8Array[3] === 0x04) || (uint8Array[2] === 0x05 && uint8Array[3] === 0x06)

  if (!isValidZip) {
    return false
  }

  // Validate that the file is a valid Excel file using XLSX library
  try {
    const workbook = XLSX.read(data, {type: 'array'})

    // Verify that the workbook has at least one sheet
    if (!workbook.SheetNames || !workbook.SheetNames.length) {
      return false
    }

    return true
  } catch (err) {
    return false
  }
}
